import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const RUNTIME_CONFIG_ID = 'ShopifyOptionRuntimeConfig';
const DESCRIPTION_SELECTOR = '[data-shopify-option-description]';
const FORM_TARGET_SELECTOR = '[data-shopify-option-form-target]';
const UI_SELECTOR = '[data-shopify-option-ui]';
const REMOVE_ALL_SELECTOR = '[data-shopify-option-remove-all]';
const OPTION_TAG = 'shopify-option';

let runtimeConfig;
let optionConfig;
let optionAddInProgress = false;
const collectionCache = new Map();

function parseRuntimeConfig() {
  const element = document.getElementById(RUNTIME_CONFIG_ID);
  if (!element) return null;

  try {
    return JSON.parse(element.textContent || '{}');
  } catch (error) {
    console.error('[shopify-option] Runtime configuration is invalid.', error);
    return null;
  }
}

async function loadOptionConfig() {
  if (optionConfig) return optionConfig;
  if (!runtimeConfig?.configUrl) throw new Error('Option configuration URL is missing.');

  const response = await fetch(runtimeConfig.configUrl, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-cache',
  });

  if (!response.ok) {
    throw new Error(`Option configuration could not be loaded (${response.status}).`);
  }

  optionConfig = await response.json();
  return optionConfig;
}

function normalizedUrl(value) {
  if (!value) return '';

  try {
    const url = new URL(value, window.location.origin);
    return `${url.hostname.toLowerCase()}${decodeURIComponent(url.pathname)}`.replace(/\/+$/, '');
  } catch {
    return String(value)
      .trim()
      .replace(/^https?:\/\//i, '')
      .replace(/^\/\//, '')
      .split(/[?#]/, 1)[0]
      .replace(/\/+$/, '')
      .toLowerCase();
  }
}

function getImageCandidates(image) {
  const values = [
    image.currentSrc,
    image.src,
    image.getAttribute('src'),
    image.getAttribute('data-src'),
    image.getAttribute('data-original'),
  ];

  const srcset = image.getAttribute('srcset');
  if (srcset) {
    for (const candidate of srcset.split(',')) {
      values.push(candidate.trim().split(/\s+/)[0]);
    }
  }

  return values.filter(Boolean).map(normalizedUrl);
}

function getInsertionTarget(image) {
  const figure = image.closest('figure');
  if (figure) return figure;

  const paragraph = image.closest('p');
  if (paragraph && paragraph.querySelectorAll('img').length === 1) return paragraph;

  const link = image.closest('a');
  if (link && link.parentElement?.childElementCount === 1) return link.parentElement;

  return image;
}

function collectionProductsUrl(handle) {
  const root = String(runtimeConfig.rootUrl || '/').replace(/\/?$/, '/');
  return `${root}collections/${encodeURIComponent(handle)}/products.json?limit=250`;
}

async function fetchCollectionProducts(handle) {
  if (collectionCache.has(handle)) return collectionCache.get(handle);

  const promise = fetch(collectionProductsUrl(handle), {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Collection "${handle}" could not be loaded (${response.status}).`);
      }
      return response.json();
    })
    .then((payload) => Array.isArray(payload.products) ? payload.products : []);

  collectionCache.set(handle, promise);
  return promise;
}

function productImage(product) {
  if (product.image?.src) return product.image.src;
  if (Array.isArray(product.images) && product.images[0]?.src) return product.images[0].src;
  return '';
}

function firstVariant(product) {
  return Array.isArray(product.variants) ? product.variants[0] : null;
}

function variantAvailable(variant) {
  return Boolean(variant && variant.available !== false);
}

function formatMoney(value) {
  // /collections/{handle}/products.json の variant.price は、
  // 日本円では「704.00」または 704 のように通貨単位で返る。
  // Ajax Cart API の「最小通貨単位」と混同して100で割らない。
  const amount = Number(value || 0);
  const locale = runtimeConfig.locale || 'ja-JP';

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'JPY',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `¥${Math.round(amount).toLocaleString('ja-JP')}`;
  }
}

function setStatus(ui, message, isError = false) {
  let status = ui.querySelector('[data-shopify-option-status]');
  if (!status) {
    status = document.createElement('p');
    status.dataset.shopifyOptionStatus = '';
    ui.append(status);
  }

  status.className = isError ? 'shopify-option-ui__error' : 'shopify-option-ui__status';
  status.textContent = message;
}

function createUiShell(groupKey, settings, location = 'description') {
  const ui = document.createElement('section');
  ui.className = `shopify-option-ui shopify-option-ui--${location}`;
  ui.dataset.shopifyOptionUi = '';
  ui.dataset.optionGroup = groupKey;
  ui.dataset.selectionType = settings.selectionType;
  ui.dataset.optionLocation = location;
  ui.setAttribute('aria-busy', 'true');

  const title = document.createElement('h3');
  title.className = 'shopify-option-ui__title';
  title.textContent = settings.uiTitle || groupKey;
  ui.append(title);

  setStatus(ui, '選択肢を読み込んでいます。');
  return ui;
}

function commonTitlePrefix(products) {
  const titles = products
    .map((product) => String(product.title || '').trim())
    .filter(Boolean);

  if (titles.length < 2) return '';

  let prefix = titles[0];
  for (const title of titles.slice(1)) {
    while (prefix && !title.startsWith(prefix)) {
      prefix = prefix.slice(0, -1);
    }
    if (!prefix) return '';
  }

  // Titleは「追加時商品名 + 半角スペース + 追加時商品オプション」で生成される。
  // 共通文字列の途中では切らず、最後の半角/全角スペースまでを共通名として除去する。
  const boundary = Math.max(prefix.lastIndexOf(' '), prefix.lastIndexOf('　'));
  if (boundary < 0) return '';

  return prefix.slice(0, boundary + 1);
}

function compactProductTitle(product, prefix) {
  const title = String(product.title || '').trim();
  if (!prefix || !title.startsWith(prefix)) return title;
  return title.slice(prefix.length).trim() || title;
}

function renderCheckboxUi(ui, products) {
  const status = ui.querySelector('[data-shopify-option-status]');
  status?.remove();

  const titlePrefix = commonTitlePrefix(products);
  const list = document.createElement('ul');
  list.className = 'shopify-option-ui__list';

  for (const product of products) {
    const variant = firstVariant(product);
    if (!variant) continue;

    const displayTitle = compactProductTitle(product, titlePrefix);
    const available = variantAvailable(variant);
    const item = document.createElement('li');
    item.className = 'shopify-option-ui__choice';

    const label = document.createElement('label');
    label.className = 'shopify-option-ui__label';

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.className = 'shopify-option-ui__checkbox';
    input.value = String(variant.id);
    input.dataset.optionVariantId = '';
    input.disabled = !available;
    input.setAttribute('aria-label', displayTitle);

    const name = document.createElement('span');
    name.className = 'shopify-option-ui__product-title';
    name.textContent = displayTitle;

    const meta = document.createElement('span');
    meta.className = 'shopify-option-ui__product-meta';

    const price = document.createElement('span');
    price.className = 'shopify-option-ui__product-price';
    price.textContent = formatMoney(variant.price);
    meta.append(price);

    if (!available) {
      const soldOut = document.createElement('span');
      soldOut.className = 'shopify-option-ui__sold-out';
      soldOut.textContent = '在庫切れ';
      meta.append(soldOut);
    }

    label.append(input, name, meta);
    item.append(label);
    list.append(item);
  }

  ui.append(list);
}

function renderSelectUi(ui, products) {
  const status = ui.querySelector('[data-shopify-option-status]');
  status?.remove();

  const titlePrefix = commonTitlePrefix(products);
  const select = document.createElement('select');
  select.className = 'shopify-option-ui__select';
  select.dataset.optionVariantSelect = '';
  select.setAttribute('aria-label', ui.querySelector('.shopify-option-ui__title')?.textContent || 'オプション');

  const none = document.createElement('option');
  none.value = '';
  none.textContent = '不要';
  select.append(none);

  for (const product of products) {
    const variant = firstVariant(product);
    if (!variant) continue;

    const available = variantAvailable(variant);
    const displayTitle = compactProductTitle(product, titlePrefix);
    const option = document.createElement('option');
    option.value = String(variant.id);
    option.textContent = `${displayTitle}（${formatMoney(variant.price)}）${available ? '' : '［在庫切れ］'}`;
    option.disabled = !available;
    select.append(option);
  }

  ui.append(select);
}

function renderProducts(ui, settings, products) {
  if (products.length === 0) {
    setStatus(ui, '現在選択できるオプション商品がありません。');
    return;
  }

  if (settings.selectionType === 'checkbox') {
    renderCheckboxUi(ui, products);
  } else if (settings.selectionType === 'select') {
    renderSelectUi(ui, products);
  } else {
    setStatus(ui, `未対応の選択形式です: ${settings.selectionType}`, true);
  }
}

function optionProductsOnly(products) {
  return products.filter((product) => {
    const tags = Array.isArray(product.tags)
      ? product.tags
      : String(product.tags || '').split(',').map((tag) => tag.trim());
    return tags.includes(OPTION_TAG);
  });
}

async function initializeDescription(description, config) {
  if (!(description instanceof HTMLElement) || description.dataset.shopifyOptionInitialized === 'true') return;
  description.dataset.shopifyOptionInitialized = 'true';

  const images = Array.from(description.querySelectorAll('img'));
  const formTarget = document.querySelector(FORM_TARGET_SELECTOR);

  // 商品フォーム側の表示順はJSONのキー順ではなく、
  // 商品説明内でトリガー画像が実際に出現する順番を採用する。
  // これにより固定のgroup配列やdisplay_order管理を不要にする。
  const detectedGroups = [];
  const detectedGroupKeys = new Set();

  for (const image of images) {
    const candidates = getImageCandidates(image);

    for (const [groupKey, settings] of Object.entries(config)) {
      if (
        detectedGroupKeys.has(groupKey) ||
        !settings?.triggerImageUrl ||
        !settings?.collectionHandle
      ) {
        continue;
      }

      if (!candidates.includes(normalizedUrl(settings.triggerImageUrl))) continue;

      detectedGroups.push({ groupKey, settings, image });
      detectedGroupKeys.add(groupKey);
      break;
    }
  }

  for (const { groupKey, settings, image } of detectedGroups) {
    const target = getInsertionTarget(image);

    if (target.parentElement?.querySelector(`${UI_SELECTOR}[data-option-group="${CSS.escape(groupKey)}"]`)) {
      continue;
    }

    const descriptionUi = createUiShell(groupKey, settings, 'description');
    target.insertAdjacentElement('afterend', descriptionUi);

    let formUi = null;
    if (
      formTarget instanceof HTMLElement &&
      !formTarget.querySelector(`${UI_SELECTOR}[data-option-group="${CSS.escape(groupKey)}"]`)
    ) {
      formUi = createUiShell(groupKey, settings, 'form');
      formTarget.append(formUi);
    }

    try {
      const products = optionProductsOnly(await fetchCollectionProducts(settings.collectionHandle));
      renderProducts(descriptionUi, settings, products);
      if (formUi) renderProducts(formUi, settings, products);
    } catch (error) {
      console.error('[shopify-option] Failed to render option UI.', error);
      const message = 'オプション商品の読み込みに失敗しました。ページを再読み込みしてください。';
      setStatus(descriptionUi, message, true);
      if (formUi) setStatus(formUi, message, true);
    } finally {
      descriptionUi.setAttribute('aria-busy', 'false');
      formUi?.setAttribute('aria-busy', 'false');
    }
  }
}

function synchronizeOptionSelection(source) {
  const ui = source.closest(UI_SELECTOR);
  if (!(ui instanceof HTMLElement)) return;

  const groupKey = ui.dataset.optionGroup;
  if (!groupKey) return;

  const escapedGroup = CSS.escape(groupKey);

  if (source.matches('input[data-option-variant-id]')) {
    const escapedValue = CSS.escape(source.value);
    for (const peer of document.querySelectorAll(
      `${UI_SELECTOR}[data-option-group="${escapedGroup}"] input[data-option-variant-id][value="${escapedValue}"]`
    )) {
      if (peer !== source) peer.checked = source.checked;
    }
    return;
  }

  if (source.matches('select[data-option-variant-select]')) {
    for (const peer of document.querySelectorAll(
      `${UI_SELECTOR}[data-option-group="${escapedGroup}"] select[data-option-variant-select]`
    )) {
      if (peer !== source) peer.value = source.value;
    }
  }
}

function handleOptionChange(event) {
  const source = event.target;
  if (!(source instanceof HTMLInputElement || source instanceof HTMLSelectElement)) return;
  if (!source.matches('input[data-option-variant-id], select[data-option-variant-select]')) return;
  synchronizeOptionSelection(source);
}

function selectedOptionVariantIds() {
  const ids = [];

  for (const ui of document.querySelectorAll(UI_SELECTOR)) {
    for (const checkbox of ui.querySelectorAll('input[data-option-variant-id]:checked:not(:disabled)')) {
      if (checkbox.value) ids.push(checkbox.value);
    }

    for (const select of ui.querySelectorAll('select[data-option-variant-select]')) {
      if (select.value && !select.selectedOptions[0]?.disabled) ids.push(select.value);
    }
  }

  return [...new Set(ids)];
}

function clearSelections() {
  for (const checkbox of document.querySelectorAll('input[data-option-variant-id]')) {
    checkbox.checked = false;
  }

  for (const select of document.querySelectorAll('select[data-option-variant-select]')) {
    select.value = '';
  }
}

function sectionIds() {
  const ids = new Set(['cart-drawer-section']);
  for (const component of document.querySelectorAll('cart-items-component[data-section-id]')) {
    if (component instanceof HTMLElement && component.dataset.sectionId) {
      ids.add(component.dataset.sectionId);
    }
  }
  return [...ids];
}

async function fetchCart() {
  const cartUrl = String(runtimeConfig.cartUrl || '/cart').replace(/\/$/, '');
  const response = await fetch(`${cartUrl}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
  });

  if (!response.ok) throw new Error(`Cart could not be loaded (${response.status}).`);
  return response.json();
}

function dispatchCartMutation({ action, lines, request }) {
  const deferred = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'product',
      lines,
      promise: deferred.promise,
    })
  );

  request(deferred);
  return deferred.promise;
}

function showOptionError(message) {
  const firstUi = document.querySelector(UI_SELECTOR);
  if (firstUi instanceof HTMLElement) {
    setStatus(firstUi, message, true);
    firstUi.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
}

async function addOptionProducts(variantIds) {
  if (variantIds.length === 0 || optionAddInProgress) return;
  optionAddInProgress = true;

  const lines = variantIds.map((id) => ({ merchandiseId: String(id), quantity: 1 }));
  const ids = sectionIds();
  const payload = {
    items: variantIds.map((id) => ({ id: Number(id), quantity: 1 })),
    sections: ids.join(','),
    sections_url: window.location.pathname,
  };

  try {
    await dispatchCartMutation({
      action: 'add',
      lines,
      request: async (deferred) => {
        try {
          const response = await fetch(runtimeConfig.cartAddUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            credentials: 'same-origin',
            body: JSON.stringify(payload),
          });

          const result = await response.json();
          if (!response.ok || result.status) {
            throw new Error(result.description || result.message || 'オプション商品を追加できませんでした。');
          }

          const cart = await fetchCart();
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: {
              items: cart.items,
              source: 'shopify-option-ui',
              sourceId: 'shopify-option-ui',
              itemCount: variantIds.length,
              sections: result.sections,
              didError: false,
            },
          });
          clearSelections();
        } catch (error) {
          deferred.reject(error);
        }
      },
    });
  } catch (error) {
    console.error('[shopify-option] Add failed.', error);
    showOptionError(`通常商品はカートに追加されましたが、オプション商品の追加に失敗しました。${error.message || ''}`);
  } finally {
    optionAddInProgress = false;
  }
}

function handleProductCartEvent(event) {
  if (event.action !== 'add' || optionAddInProgress) return;
  if (!(event.target instanceof Element) || !event.target.matches('product-form-component')) return;

  const variantIds = selectedOptionVariantIds();
  if (variantIds.length === 0 || !event.promise) return;

  event.promise
    .then(({ detail }) => {
      if (detail?.didError) return;
      return addOptionProducts(variantIds);
    })
    .catch(() => {
      // The parent product was not added; do not add options.
    });
}

async function removeAllOptionItems(button) {
  const keys = String(button.dataset.optionLineKeys || '')
    .split('|')
    .map((key) => key.trim())
    .filter(Boolean);

  if (keys.length === 0) return;

  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  const updates = Object.fromEntries(keys.map((key) => [key, 0]));
  const ids = sectionIds();
  const lines = keys.map((key) => ({ id: key, quantity: 0 }));

  try {
    await dispatchCartMutation({
      action: 'remove',
      lines,
      request: async (deferred) => {
        try {
          const response = await fetch(runtimeConfig.cartUpdateUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            credentials: 'same-origin',
            body: JSON.stringify({
              updates,
              sections: ids.join(','),
              sections_url: window.location.pathname,
            }),
          });

          const result = await response.json();
          if (!response.ok || result.status) {
            throw new Error(result.description || result.message || 'オプション商品を削除できませんでした。');
          }

          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result),
            detail: {
              items: result.items,
              source: 'shopify-option-ui',
              sourceId: 'shopify-option-remove-all',
              sections: result.sections,
              didError: false,
            },
          });
        } catch (error) {
          deferred.reject(error);
        }
      },
    });
  } catch (error) {
    console.error('[shopify-option] Remove all failed.', error);
    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.textContent = '削除に失敗しました。もう一度お試しください';
  }
}

function handleClick(event) {
  const button = event.target instanceof Element ? event.target.closest(REMOVE_ALL_SELECTOR) : null;
  if (!(button instanceof HTMLButtonElement)) return;
  removeAllOptionItems(button);
}

async function initialize() {
  runtimeConfig = parseRuntimeConfig();
  if (!runtimeConfig) return;

  document.addEventListener(StandardEvents.cartLinesUpdate, handleProductCartEvent, true);
  document.addEventListener('change', handleOptionChange);
  document.addEventListener('click', handleClick);

  if (runtimeConfig.isOptionProduct) return;

  try {
    const config = await loadOptionConfig();
    for (const description of document.querySelectorAll(DESCRIPTION_SELECTOR)) {
      initializeDescription(description, config);
    }
  } catch (error) {
    console.error('[shopify-option] Initialization failed.', error);
  }
}

initialize();
